import { computed, ref, type Ref } from 'vue'

import { type CanvasRetrieve } from '@openapi'
import {
  type ComponentNode,
  type SectionOverrides,
  type SectionComponentOverride,
  type SectionComponentOverrides,
} from '@canvas-builder/types/canvas'
import { findNodeInTreeById } from '@canvas-builder/util/tree'
import useCanvas from '@canvas-builder/composables/useCanvas'

const sectionComponentOverrides = ref<SectionComponentOverrides>({})

const getComponentOverrides = (
  data: Ref<ComponentNode['data']>,
  id: Ref<ComponentNode['id']>,
  sectionId?: Ref<CanvasRetrieve['id']>,
) => {
  return computed(() => {
    if (!sectionId || !sectionComponentOverrides.value[sectionId.value]?.[id.value]) {
      return { data: data.value, is_removed: false }
    }

    return {
      data: sectionComponentOverrides.value[sectionId.value][id.value].node?.data,
      is_removed: !!sectionComponentOverrides.value[sectionId.value][id.value].is_removed,
    }
  })
}

function discardOverridesConfirmationOptions(t: (input: string) => string, callback: () => void) {
  return {
    title: t('canvasUI.canvasBuilder.confirmationOnDiscardOverrides.title'),
    content: t('canvasUI.canvasBuilder.confirmationOnDiscardOverrides.content'),
    confirmText: t('canvasUI.canvasBuilder.confirmationOnDiscardOverrides.confirm'),
    cancelText: t('canvasUI.canvasBuilder.confirmationOnDiscardOverrides.cancel'),
    hasExit: false,
    onConfirm: async () => callback(),
  }
}

const overrideSectionComponentData = ({
  usedInSectionId,
  componentId,
  componentData,
  afterSiblingId,
  remove,
}: {
  usedInSectionId: CanvasRetrieve['id']
  componentId: ComponentNode['id']
  componentData?: ComponentNode['data']
  afterSiblingId?: ComponentNode['id']
  remove?: boolean
}) => {
  const { activeCanvasSections, myUserId } = useCanvas()

  if (!usedInSectionId || !componentId) {
    console.warn(`Missing required parameters for overrideSectionData`)
    return
  }

  const sectionContent = (activeCanvasSections.value?.[usedInSectionId]?.content?.data ?? []) as ComponentNode[]
  const componentNode = findNodeInTreeById(sectionContent, componentId)
  if (!componentNode) {
    console.error(`Could not find component node with id: ${componentId}`)
    return
  }

  const override = (() => {
    let o: SectionComponentOverride
    if (remove) {
      o = { is_removed: true }
      return o
    }

    o = {
      node: componentData ? { ...componentNode, data: componentData } : componentNode,
    }
    componentId === 'root' && (o.after_sibling_id = afterSiblingId)
    return o
  })()

  sectionComponentOverrides.value = {
    ...sectionComponentOverrides.value,
    [usedInSectionId]: {
      ...sectionComponentOverrides.value[usedInSectionId],
      [componentId]: override,
      ...(myUserId.value
        ? {
            modified_by: { id: myUserId.value },
            modified_at: new Date().toISOString(),
          }
        : {}),
    } as SectionOverrides,
  }
}

function setSectionOverrides(sectionId: CanvasRetrieve['id'], overrides?: SectionOverrides) {
  if (!overrides) {
    delete sectionComponentOverrides.value[sectionId]

    return
  }

  sectionComponentOverrides.value = {
    ...sectionComponentOverrides.value,
    [sectionId]: overrides,
  }
}

function cleanup() {
  sectionComponentOverrides.value = {}
}

export default function useCanvasSectionOverrides() {
  return {
    getComponentOverrides,
    discardOverridesConfirmationOptions,
    overrideSectionComponentData,
    cleanup,
    sectionComponentOverrides,
    setSectionOverrides,
  }
}
